/**
 * File helpers for Flux Agent uploads and trainer playbook imports. Normalizes browser-provided
 * relative paths into the server's document path rules (`[A-Za-z0-9._/-]`, `.md` suffix, no `..`,
 * no leading slash, at most 200 characters), fills in media types the browser leaves blank, and
 * formats byte sizes. All functions are pure.
 *
 * @module apps/web/src/lib/fluxAgentFiles
 */

/** Longest document path the server accepts. */
export const MAX_DOCUMENT_PATH = 200;

/** Most documents accepted by one import request. */
export const MAX_IMPORT_DOCUMENTS = 200;

/** Longest document body the server accepts, in characters. */
export const MAX_DOCUMENT_CHARS = 200_000;

/** True when `path` satisfies the server's document path rules. */
export function isValidDocumentPath(path: string): boolean {
  return (
    path.length >= 1 &&
    path.length <= MAX_DOCUMENT_PATH &&
    /^[A-Za-z0-9._/-]+$/.test(path) &&
    path.endsWith('.md') &&
    !path.startsWith('/') &&
    !path.split('/').some(segment => segment === '..' || segment === '')
  );
}

/**
 * Turn a user-typed or browser-relative path into a valid document path, or `null` if nothing
 * usable remains. Backslashes become slashes, whitespace and other disallowed characters become
 * hyphens, `.` and `..` segments are dropped, and `.markdown` is rewritten to `.md`.
 */
export function normalizeDocumentPath(raw: string): string | null {
  const segments = raw
    .replace(/\\/g, '/')
    .split('/')
    .map(segment =>
      segment
        .trim()
        .replace(/[^A-Za-z0-9._-]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '')
    )
    .filter(segment => segment && segment !== '.' && segment !== '..');
  if (!segments.length) return null;
  let path = segments.join('/').replace(/\.markdown$/i, '.md');
  if (/\.md$/i.test(path)) path = `${path.slice(0, -3)}.md`;
  return isValidDocumentPath(path) ? path : null;
}

/** A file selected for import with its browser-relative path and text. */
export interface ImportSource {
  /** `webkitRelativePath` for folder picks (`folder/sub/file.md`) or the bare file name. */
  relativePath: string;
  /** Decoded file text; bodies over {@link MAX_DOCUMENT_CHARS} characters are skipped. */
  content: string;
}

/** Result of {@link buildImportDocuments}. */
export interface ImportPlan {
  /** Documents to send, sorted by path and capped at {@link MAX_IMPORT_DOCUMENTS}. */
  documents: {
    /** Normalized document path relative to the picked folder. */
    path: string;
    /** File text sent unchanged as the document body. */
    content: string;
  }[];
  /** Human-readable reasons for each skipped source. */
  skipped: string[];
}

/**
 * Build `{path, content}` import documents. When every source shares the same top-level folder
 * (a folder pick), that folder name is removed so paths are relative to the chosen folder. Non-
 * Markdown files, invalid paths, oversize bodies, duplicates (last one wins), and anything past
 * the 200-document request limit are reported in `skipped`.
 */
export function buildImportDocuments(sources: ImportSource[]): ImportPlan {
  const skipped: string[] = [];
  const split = sources.map(source =>
    source.relativePath.replace(/\\/g, '/').split('/').filter(Boolean)
  );
  const roots = new Set(split.map(parts => (parts.length > 1 ? parts[0] : '')));
  const stripRoot = roots.size === 1 && !roots.has('');
  const byPath = new Map<string, string>();

  sources.forEach((source, index) => {
    const parts = stripRoot ? split[index].slice(1) : split[index];
    const original = source.relativePath || '(unnamed)';
    if (!/\.(md|markdown)$/i.test(original)) {
      skipped.push(`${original}: not a Markdown file`);
      return;
    }
    const path = normalizeDocumentPath(parts.join('/'));
    if (!path) {
      skipped.push(`${original}: path cannot be used`);
      return;
    }
    if (source.content.length > MAX_DOCUMENT_CHARS) {
      skipped.push(`${original}: longer than ${MAX_DOCUMENT_CHARS.toLocaleString()} characters`);
      return;
    }
    if (byPath.has(path)) skipped.push(`${original}: duplicate of ${path}, later file kept`);
    byPath.set(path, source.content);
  });

  const documents = [...byPath.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([path, content]) => ({ path, content }));
  if (documents.length > MAX_IMPORT_DOCUMENTS) {
    documents
      .slice(MAX_IMPORT_DOCUMENTS)
      .forEach(doc => skipped.push(`${doc.path}: over the ${MAX_IMPORT_DOCUMENTS}-file limit`));
    documents.length = MAX_IMPORT_DOCUMENTS;
  }
  return { documents, skipped };
}

/** Media types keyed by lowercase extension; also the source of {@link UPLOAD_ACCEPT}. */
const EXTENSION_TYPES: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  csv: 'text/csv',
  json: 'application/json',
  html: 'text/html',
  htm: 'text/html',
  srt: 'application/x-subrip',
  vtt: 'text/vtt',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};

/** Media type for an upload, inferring from the extension when the browser reports none. */
export function guessMimeType(name: string, reported?: string): string {
  if (reported) return reported;
  const extension = name.split('.').pop()?.toLowerCase() || '';
  return EXTENSION_TYPES[extension] || 'application/octet-stream';
}

/** File input `accept` list matching the upload types the server documents. */
export const UPLOAD_ACCEPT = [
  ...Object.keys(EXTENSION_TYPES).map(extension => `.${extension}`),
  'text/*',
  'image/*',
].join(',');

/** Compact size label such as `512 B`, `12 KB`, or `4.5 MB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes >= 10 ? Math.round(megabytes) : megabytes.toFixed(1)} MB`;
}
